import React, { useEffect, useState } from "react";
import { Save, Loader2, Plus, Trash2, ChevronDown } from "lucide-react";
import { api, assetUrl } from "../../adminApi";
import { Card, SectionTitle, Field, Input, Btn, LocalizedInput } from "../../components/admin/AdminUI";
import { ImageUploader, PdfUploader } from "../../components/admin/MediaUploader";

const empty = {
  key: "",
  name: "",
  no: "",
  type: { en: "", fi: "" },
  headline: { en: "", fi: "" },
  character: { en: "", fi: "" },
  modules: "",
  panel: "",
  layouts: "",
  cover: "",
  gallery: [],
  parent: "",
  menu_parent: "/products",
  pdf_url: "",
  pdf_name: "",
  pdf_updated_at: "",
  order: 0,
  visible: true,
};

function CollectionRow({ item, onSaved, onDeleted, openByDefault, parents = [] }) {
  const [open, setOpen] = useState(!!openByDefault);
  const [form, setForm] = useState({ ...empty, ...item });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [pdfMsg, setPdfMsg] = useState("");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setEv = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // PDF changes persist to MongoDB immediately for saved collections
  const savePdf = async (url, fileName = "") => {
    setPdfMsg("");
    if (!item.id) {
      setForm((f) => ({ ...f, pdf_url: url, pdf_name: fileName, pdf_updated_at: url ? new Date().toISOString() : "" }));
      setPdfMsg(url ? "KOLEKSİYONU KAYDEDİNCE YAYINLANACAK" : "");
      return;
    }
    const saved = await api.setCollectionPdf(item.id, url, fileName);
    setForm((f) => ({ ...f, pdf_url: saved.pdf_url, pdf_name: saved.pdf_name, pdf_updated_at: saved.pdf_updated_at }));
    setPdfMsg(url ? "KAYDEDİLDİ VE SİTEDE YAYINDA" : "PDF KALDIRILDI");
    onSaved(saved);
  };

  const save = async () => {
    setBusy(true);
    setMsg("");
    const body = { ...form, order: Number(form.order) || 0 };
    delete body.id;
    try {
      const saved = item.id ? await api.updateCollection(item.id, body) : await api.createCollection(body);
      setMsg("Kaydedildi ve sitede yayında.");
      onSaved(saved);
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`"${form.name}" koleksiyonunu silmek istediğinize emin misiniz?`)) return;
    await api.deleteCollection(item.id);
    onDeleted();
  };

  return (
    <Card className="mb-3" data-testid={`collection-${form.key || "new"}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-[#FAFAFA] transition-colors"
        data-testid={`collection-toggle-${form.key || "new"}`}
      >
        <div className="w-[64px] h-[46px] bg-[#F2F0EB] border border-[#E2E0D8] overflow-hidden shrink-0">
          {(form.cover || form.gallery?.[0]) && (
            <img src={assetUrl(form.cover || form.gallery[0])} alt="" className="w-full h-full object-cover" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="display text-[20px] text-[#141414] leading-tight">{form.name || "Yeni koleksiyon"}</p>
          <p className="mono text-[8.5px] text-[#A8A29A] mt-1">
            {form.no ? `${form.no} · ` : ""}
            {form.parent ? `ALT ÜRÜN (${form.parent}) · ` : "ANA KATEGORİ · "}
            {form.gallery?.length || 0} görsel · {form.pdf_url ? "PDF VAR" : "PDF YOK"}
          </p>
        </div>
        <ChevronDown size={16} strokeWidth={1.7} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-5 lg:px-7 pb-7 pt-2 border-t border-[#E2E0D8] space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Field label="KOLEKSİYON ADI">
              <Input value={form.name} onChange={setEv("name")} data-testid="collection-name" />
            </Field>
            <Field label="NO">
              <Input value={form.no} onChange={setEv("no")} />
            </Field>
            <Field label="ANAHTAR (URL)" hint="Boş bırakılırsa isimden üretilir">
              <Input value={form.key} onChange={setEv("key")} />
            </Field>
            <Field label="SIRA NO">
              <Input type="number" value={form.order} onChange={setEv("order")} />
            </Field>
          </div>

          <Field
            label="ÜST KATEGORİ"
            hint="Boş bırakılırsa bu koleksiyon bir ANA KATEGORİ olur ve menüde otomatik görünür."
          >
            <select
              value={form.parent || ""}
              onChange={setEv("parent")}
              data-testid={`collection-parent-${form.key || "new"}`}
              className="w-full bg-[#FAFAFA] border border-[#E2E0D8] px-3.5 py-3 text-[14px] text-[#141414] outline-none focus:border-[#C8B29B]"
            >
              <option value="">— ANA KATEGORİ (üst seviye) —</option>
              {parents
                .filter((p) => p.key !== form.key)
                .map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.name || p.key}
                  </option>
                ))}
            </select>
          </Field>

          <Field
            label="HANGİ MENÜNÜN ALTINDA GÖRÜNSÜN"
            hint="Ana kategoriler seçilen menünün açılır listesinde otomatik görünür."
          >
            <select
              value={form.menu_parent === undefined ? "/products" : form.menu_parent}
              onChange={setEv("menu_parent")}
              data-testid={`collection-menuparent-${form.key || "new"}`}
              className="w-full bg-[#FAFAFA] border border-[#E2E0D8] px-3.5 py-3 text-[14px] text-[#141414] outline-none focus:border-[#C8B29B]"
            >
              <option value="/products">Ürünler (/products)</option>
              <option value="/portfolio">Portföy (/portfolio)</option>
              <option value="">Menüde gösterme</option>
            </select>
          </Field>

          <LocalizedInput label="TÜR / KARAKTER ETİKETİ" value={form.type} onChange={set("type")} />
          <LocalizedInput label="BAŞLIK CÜMLESİ" value={form.headline} onChange={set("headline")} />
          <LocalizedInput label="AÇIKLAMA" value={form.character} onChange={set("character")} multiline rows={5} />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="MODÜLLER">
              <Input value={form.modules} onChange={setEv("modules")} />
            </Field>
            <Field label="PANEL ÖLÇÜSÜ">
              <Input value={form.panel} onChange={setEv("panel")} />
            </Field>
            <Field label="YERLEŞİM SEÇENEKLERİ">
              <Input value={form.layouts} onChange={setEv("layouts")} />
            </Field>
          </div>

          <ImageUploader
            images={form.gallery}
            onChange={(g) => setForm((f) => ({ ...f, gallery: g, cover: f.cover || g[0] || "" }))}
            testid={`collection-gallery-${form.key || "new"}`}
          />

          <PdfUploader
            value={form.pdf_url}
            fileName={form.pdf_name}
            updatedAt={form.pdf_updated_at}
            status={pdfMsg}
            onChange={savePdf}
            testid={`collection-pdf-${form.key || "new"}`}
          />

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.visible}
              onChange={(e) => setForm((f) => ({ ...f, visible: e.target.checked }))}
              className="w-4 h-4 accent-[#141414]"
            />
            <span className="mono text-[9px] text-[#141414]">SİTEDE GÖRÜNSÜN</span>
          </label>

          {msg && <p className="mono text-[9px] text-[#8C7862]">{msg}</p>}

          <div className="flex gap-3">
            <Btn onClick={save} disabled={busy} data-testid={`collection-save-${form.key || "new"}`}>
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} strokeWidth={1.7} />}
              {busy ? "KAYDEDİLİYOR..." : "KAYDET"}
            </Btn>
            {item.id && (
              <Btn variant="danger" onClick={remove}>
                <Trash2 size={13} strokeWidth={1.7} /> SİL
              </Btn>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function AdminCollections() {
  const [list, setList] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = () => api.adminCollections().then(setList).catch(() => setList([]));
  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="admin-collections">
      <SectionTitle
        label="[ ÜRÜNLER ]"
        title="Koleksiyonlar"
        right={
          <Btn onClick={() => setAdding(true)} data-testid="collection-new">
            <Plus size={13} strokeWidth={1.8} /> YENİ KOLEKSİYON
          </Btn>
        }
      />

      {adding && (
        <CollectionRow
          item={{}}
          parents={(list || []).filter((x) => !x.parent)}
          openByDefault
          onSaved={() => {
            setAdding(false);
            load();
          }}
          onDeleted={() => setAdding(false)}
        />
      )}

      {!list ? (
        <Loader2 size={18} className="animate-spin text-[#8C7862]" />
      ) : (
        list.map((c) => (
          <CollectionRow
            key={c.id}
            item={c}
            parents={list.filter((x) => !x.parent)}
            onSaved={load}
            onDeleted={load}
          />
        ))
      )}
    </div>
  );
}
